
import { Component, Mixins } from "vue-property-decorator";
import GenericTable, { TableHeader } from '../table/generic-table.vue';
import { ModalItem } from '../table/generic-update-modal.vue';
import { PaginatedSearchQueryParams } from '@/store/types'
import organizationApi from "@/services/api.organizationSettings";
import PaginatedSearch, { PaginatedSearchRequest } from '@/mixins/PaginatedSearch';
import AreaTreeSelect from "../form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import { GroupDateFormatEntry, GroupDateFormatSaveRequest, GroupDateFormatOption } from "@/types/organization-settings/GroupDateFormat";

@Component({
	components: {
		"generic-table": GenericTable,
	}
})
export default class NotificationDateTimeFormatSetup extends Mixins(PaginatedSearch){
	private isLoading: boolean = false;
	private totalRecords: number = 0;
	private paginatedSearchQueryParams: PaginatedSearchQueryParams = {
		searchTerm: null,
		page: 1,
		pageSize: 25
	};

	private records: GroupDateFormatEntry[] = [];

    private get options(): GroupDateFormatOption[] {
		return [
			{ title: 'MM-DD-YYYY', dateFormat: 'MM-dd-yyyy HH:mm:ss' },
			{ title: 'DD-MM-YYYY', dateFormat: 'dd-MM-yyyy HH:mm:ss' },
			{ title: 'YYYY-MM-DD', dateFormat: 'yyyy-MM-dd HH:mm:ss' },
		];
    }

	public columns: TableHeader[] = [
		{
			title: "Area",
			key: "groupTitle",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The Area Title",
			searchable: true,
			visible: true,
			dataType: "input",
			isTermLabel: true,
			sortable: true,
			isSortedByDefault: true,
		},
		{
			title: "Date Format",
			key: "dateFormatTitle",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "Any child areas will inherit the date format of their parent area, unless they have their own setting.",
			searchable: true,
			visible: true,
			dataType: "input",
			isTermLabel: false,
			sortable: false
		}
    ];

	private async save(data: GroupDateFormatSaveRequest, isUpdate = false): Promise<void> {
		try {
			const { groupId, dateFormat } = data;
			if (!isUpdate) {
				if (this.records.some(record => record.groupId === groupId)) {
					this.notify('A format for this area already exists.', 'error');
					return;
				}
			}

			await organizationApi.createOrUpdateGroupDateFormat(groupId, dateFormat);
		} catch (error) {
			console.error("Error saving Group Date Format settings:", error);
		}
	}

	private notify(message: string, type: string = "info"): void {
		this.$notify({
			type,
			text: message,
			duration: 3000
		});
	}

    private async mounted(): Promise<void> {
		await this.retrieveDateFormats();
	}

	private async retrieveDateFormats(): Promise<void> {
		this.isLoading = true;
		try {
			const pageRequest: PaginatedSearchRequest = this.generateNewPaginatedSearchRequest(this.paginatedSearchQueryParams);
			const response = await organizationApi.getGroupDateFormats(pageRequest);
			if (!response) {
				this.totalRecords = 0;
				this.records = [];
				this.isLoading = false;
				return;
			}

			this.totalRecords = response.totalRecords;
			this.records = response.data.map((item: GroupDateFormatEntry) => ({
				...item,
				dateFormatTitle: this.dateFormatToLabel(item.dateFormat),
				originalGroupId: item.groupId // Store original groupId for updates
			}));
		} catch (error) {
			console.error("Error retrieving Group Date Formats:", error);
		}
		this.isLoading = false;
	}

	private async deleteRecord(record: GroupDateFormatEntry): Promise<void> {
		try {
			await organizationApi.deleteGroupDateFormat(record.groupId);
		} catch (error) {
			console.error("Error deleting Group Date Format:", error);
		}
	}

	private async updatePaginationParameters(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		this.paginatedSearchQueryParams = paginatedSearchQueryParams;
		await this.retrieveDateFormats();
	}

	private dateFormatToLabel(dateFormat: string): string {
		return this.options.find(o => o.dateFormat === dateFormat)?.title ?? dateFormat;
	}

    public get modalItems(): ModalItem[] {
		return [
			{
				title: "Area",
				key: "groupId",
				dataType: "component",
				description: "Any child areas will inherit the layout of their parent area, unless they have their own setting.",
				required: true,
				data: AreaTreeSelect,
				props: {
					reduce: (area: AreaNode) => area.id,
					clearable: false,
					appendToBody: true,
				},
				readOnlyIfEdit: true,
            },
            {
                title: "Date Format",
                key: "dateFormat",
                dataType: "vselect3",
				required: true,
				data: this.options,
				props: {
					reduce: (option: any) => option.dateFormat,
				}
			},
            {
                title: "Original Group ID",
                key: "originalGroupId",
                dataType: "number",
				data: this.options,
				props: {
					reduce: (option: any) => option.originalGroupId,
				},
				visible: false
			},
		]
	}
}
